import camelCase from "lodash/camelCase";
import { matchPath } from "react-router-dom";
import { IBreadcrumbItem } from "dash-ui";

import { RoutesPath } from "flow-components/routes/routes";
import {
  IBreadcrumbScopeBuildParams,
  IBreadcrumbScopeBuildHookParams,
} from "common/hooks/use-breadcrumb-builder/breadcrumbs-scope-builders/";
import {
  useGetApiCredentialsByIdLazyQuery,
  useGetUserByApiCredentialsIdLazyQuery,
} from "gql/generated.graphql";
import {
  ApiTabsKeys,
  DEFAULT_API_TAB_KEY,
} from "pages/api-management/components/ApiTabs/ApiTabs.constants";

export const useApiBreadcrumbBuilder = (): [
  IBreadcrumbScopeBuildHookParams
] => {
  const [getApiCredentialsById] = useGetApiCredentialsByIdLazyQuery();
  const [getUserByApiCredentialsId] = useGetUserByApiCredentialsIdLazyQuery();

  return [
    async ({ pathname, t, currentTab }: IBreadcrumbScopeBuildParams) => {
      const items: IBreadcrumbItem[] = [];

      // Home breadcrumb for api management
      items.push({
        label: t`apiManagement`,
        path: RoutesPath.APIManagement,
        icon: "world",
      });

      // Match company details path and extract id param
      const propertiesParams: any =
        matchPath(RoutesPath.APIManagementCompanyDetails, pathname) ||
        matchPath(RoutesPath.APIManagementPluginUserDetails, pathname);

      const apiCredentialsId = propertiesParams?.params?.id;
      const apiPath = RoutesPath.APIManagement;

      if (apiCredentialsId) {
        // Builds breadcrumbs for Companies
        if (
          propertiesParams?.pattern?.path ===
          RoutesPath.APIManagementCompanyDetails
        ) {
          // Fetch company name
          const { data } = await getApiCredentialsById({
            variables: { apiCredentialsId },
          });
          const companyName = data?.getApiCredentialsById?.companyName;

          items.push({
            label: t(camelCase(ApiTabsKeys.COMPANIES)),
            path: `${apiPath}?tab=${ApiTabsKeys.COMPANIES}`,
          });

          // Replace :id in the path with the actual id
          const companyDetailsPath =
            RoutesPath.APIManagementCompanyDetails.replace(
              ":id",
              apiCredentialsId
            );

          items.push({
            label: companyName ?? "Unnamed Company",
            path: companyDetailsPath,
          });
        }

        // Builds breadcrumbs for Plugin User
        if (
          propertiesParams?.pattern?.path ===
          RoutesPath.APIManagementPluginUserDetails
        ) {
          // Fetch user name
          const { data } = await getUserByApiCredentialsId({
            variables: { filter: { apiCredentialsId } },
          });

          const firstName = data?.getUserByApiCredentialsId?.profile?.firstName;
          const lastName = data?.getUserByApiCredentialsId?.profile?.lastName;

          const fullName = `${firstName} ${lastName}`;

          items.push({
            label: t(camelCase(ApiTabsKeys.PLUGIN_USERS)),
            path: `${apiPath}?tab=${ApiTabsKeys.PLUGIN_USERS}`,
          });

          // Replace :id in the path with the actual id
          const pluginUserDetailsPath =
            RoutesPath.APIManagementPluginUserDetails.replace(
              ":id",
              apiCredentialsId
            );

          items.push({
            label: fullName,
            path: pluginUserDetailsPath,
          });
        }
      }

      if (currentTab && currentTab !== DEFAULT_API_TAB_KEY) {
        // add API Management tab as breadcrumb
        items.push({
          label: t(camelCase(currentTab)),
          path: `${apiPath}?tab=${currentTab}`,
        });
      }

      return items;
    },
  ];
};
